/**
 * Subscription Popup - Alpine.js component
 * Usage: x-data="subscriptionModal(id, hcaptchaConfig)"
 * hcaptchaConfig: { siteKey: string, size: string } — omit or pass {} when not using hCaptcha
 */
document.addEventListener("alpine:init", () => {
    Alpine.data("subscriptionModal", (modalId, hcaptchaConfig = {}) => ({
        isOpen: false,
        email: "",
        dontShowAgain: false,
        selectedLanguages: [],
        message: "",
        showMessage: false,
        isSuccess: false,
        isLoading: false,
        isFormValid: false,
        hcaptchaCompleted: !hcaptchaConfig.siteKey,
        hcaptchaWidgetId: null,
        storageKey: "modalShown",
        id: modalId,
        modalElement: null,

        getHcaptchaTheme() {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        },

        checkFormValidity() {
            let isValid = !!(this.email && this.email.includes('@'));
            if (hcaptchaConfig.siteKey && !this.hcaptchaCompleted) isValid = false;
            this.isFormValid = isValid;
            return isValid;
        },

        handleOpen(targetId) {
            if (!targetId || targetId === this.id) {
                this.isOpen = true;
                if (this.modalElement) document.body.appendChild(this.modalElement);
                document.body.style.overflow = "hidden";
            }
        },

        initialize(autoOpen = false, showDelay = 30000) {
            this.selectedLanguages = [];
            this.modalElement = this.$el;

            const portalContainer = document.getElementById("popup-portal") || document.createElement("div");
            portalContainer.id = "popup-portal";
            document.body.appendChild(portalContainer);
            portalContainer.appendChild(this.modalElement);

            if (autoOpen && !this.hasBeenDismissed()) {
                setTimeout(() => {
                    this.isOpen = true;
                    document.body.style.overflow = "hidden";
                }, showDelay);
            }

            if (hcaptchaConfig.siteKey) {
                const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
                darkModeQuery.addEventListener('change', () => {
                    if (this.hcaptchaWidgetId !== null) {
                        const container = document.getElementById(`popup-hcaptcha-container-${this.id}`);
                        if (container && typeof hcaptcha !== 'undefined') {
                            try {
                                hcaptcha.remove(this.hcaptchaWidgetId);
                                this.hcaptchaWidgetId = null;
                                this.hcaptchaCompleted = false;
                                this.checkFormValidity();
                                setTimeout(() => renderPopupCaptcha(), 100);
                            } catch (e) {
                                console.error('Error updating popup hCaptcha theme:', e);
                            }
                        }
                    }
                });

                const renderPopupCaptcha = () => {
                    const container = document.getElementById(`popup-hcaptcha-container-${this.id}`);
                    if (container && typeof hcaptcha !== 'undefined' && !container.hasChildNodes()) {
                        try {
                            this.hcaptchaWidgetId = hcaptcha.render(container, {
                                sitekey: hcaptchaConfig.siteKey,
                                size: hcaptchaConfig.size || 'compact',
                                theme: this.getHcaptchaTheme(),
                                callback: (token) => {
                                    document.getElementById(`popup-hcaptcha-response-${this.id}`).value = token;
                                    this.hcaptchaCompleted = true;
                                    this.checkFormValidity();
                                },
                                'expired-callback': () => {
                                    document.getElementById(`popup-hcaptcha-response-${this.id}`).value = '';
                                    this.hcaptchaCompleted = false;
                                    this.checkFormValidity();
                                },
                                'error-callback': () => {
                                    document.getElementById(`popup-hcaptcha-response-${this.id}`).value = '';
                                    this.hcaptchaCompleted = false;
                                    this.checkFormValidity();
                                }
                            });
                        } catch (e) {
                            console.error('Error rendering popup hCaptcha:', e);
                        }
                    }
                };

                // Load and render hCaptcha whenever the popup becomes visible
                this.$watch('isOpen', (value) => {
                    if (!value) return;
                    if (typeof hcaptcha !== 'undefined') {
                        renderPopupCaptcha();
                    } else if (!window.hcaptchaScriptLoaded) {
                        window.hcaptchaScriptLoaded = true;
                        const script = document.createElement('script');
                        script.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
                        script.async = true;
                        script.defer = true;
                        script.onload = () => {
                            window.dispatchEvent(new Event('hcaptchaLoaded'));
                            renderPopupCaptcha();
                        };
                        document.head.appendChild(script);
                    } else {
                        // Script already loading by another widget — wait for it
                        window.addEventListener('hcaptchaLoaded', renderPopupCaptcha, { once: true });
                    }
                });
            }

            this.checkFormValidity();
            this.$watch('email', () => this.checkFormValidity());
        },

        hasBeenDismissed() {
            const dismissedData = siteStorage.get(this.storageKey);
            if (!dismissedData) return false;
            try {
                const { timestamp, duration } = JSON.parse(dismissedData);
                if (Date.now() - timestamp > duration) {
                    siteStorage.remove(this.storageKey);
                    return false;
                }
                return true;
            } catch (e) {
                siteStorage.remove(this.storageKey);
                return false;
            }
        },

        setDismissed(durationMs) {
            if (durationMs) {
                siteStorage.set(this.storageKey, JSON.stringify({ timestamp: Date.now(), duration: durationMs }));
            } else {
                siteStorage.remove(this.storageKey);
            }
        },

        close() {
            this.isOpen = false;
            document.body.style.overflow = "";
            const oneDayInMs = 24 * 60 * 60 * 1000;
            const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
            this.setDismissed(this.dontShowAgain ? thirtyDaysInMs : oneDayInMs);
        },

        resetHcaptcha() {
            if (!hcaptchaConfig.siteKey) return;
            setTimeout(() => {
                try {
                    const container = document.getElementById(`popup-hcaptcha-container-${this.id}`);
                    const responseField = document.getElementById(`popup-hcaptcha-response-${this.id}`);
                    if (responseField) responseField.value = '';
                    if (typeof hcaptcha !== 'undefined' && container && this.hcaptchaWidgetId !== null) {
                        try {
                            hcaptcha.reset(this.hcaptchaWidgetId);
                        } catch (e) {
                            console.error('Error resetting hCaptcha widget:', e);
                        }
                    }
                    this.hcaptchaCompleted = false;
                    this.checkFormValidity();
                } catch (e) {
                    console.error('Error resetting popup hCaptcha:', e);
                }
            }, 100);
        },

        async submitForm() {
            this.showMessage = false;

            if (!this.email || !this.email.includes('@')) {
                this.message = 'Please enter a valid email address';
                this.isSuccess = false;
                this.showMessage = true;
                return;
            }

            if (hcaptchaConfig.siteKey) {
                const hcaptchaResponse = document.getElementById(`popup-hcaptcha-response-${this.id}`)?.value;
                if (!hcaptchaResponse) {
                    this.message = 'Please complete the captcha verification';
                    this.isSuccess = false;
                    this.showMessage = true;
                    return;
                }
            }

            this.isLoading = true;

            try {
                const payload = {
                    workflows: ['change_email_list'],
                    jinja_data: { email_address: this.email, status: 'subscribed' }
                };
                if (this.selectedLanguages.length > 0) {
                    payload.jinja_data.languages = this.selectedLanguages;
                }

                const response = await fetch(window.location.href, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                const contentType = response.headers.get('content-type');
                if (!contentType || !contentType.includes('application/json')) {
                    const text = await response.text();
                    console.error('Response body:', text.substring(0, 200));
                    throw new Error('Server returned HTML instead of JSON. Check backend configuration.');
                }

                const data = await response.json();

                if (data.result) {
                    this.message = data.result;
                    this.isSuccess = data.success === 1;
                } else if (data.message) {
                    this.message = data.message;
                    this.isSuccess = data.success === 1 || data.success === true || data.type === 'success';
                } else {
                    this.message = 'Subscription successful!';
                    this.isSuccess = true;
                }

                this.showMessage = true;

                if (this.isSuccess) {
                    if (hcaptchaConfig.siteKey) this.resetHcaptcha();
                    setTimeout(() => {
                        this.setDismissed(30 * 24 * 60 * 60 * 1000);
                        this.isOpen = false;
                        document.body.style.overflow = "";
                        this.email = "";
                        this.selectedLanguages = [];
                        this.dontShowAgain = false;
                        this.showMessage = false;
                    }, 2000);
                }
            } catch (error) {
                console.error('Newsletter subscription error:', error);
                this.message = error.message || 'An error occurred. Please try again.';
                this.isSuccess = false;
                this.showMessage = true;
                if (hcaptchaConfig.siteKey) this.resetHcaptcha();
            } finally {
                this.isLoading = false;
            }
        },

        reset() {
            siteStorage.remove(this.storageKey);
            this.isOpen = true;
            document.body.style.overflow = "hidden";
            this.email = "";
            this.selectedLanguages = [];
            this.dontShowAgain = false;
            this.message = "";
            this.showMessage = false;
            this.isSuccess = false;
            this.isLoading = false;
        }
    }));
});
